import {
  useItemLabels,
  useItems,
  type Item,
  type ItemLabel,
} from "@/api/items";
import { useLocations, type Location } from "@/api/locations";
import { usePlanningPolicies, type PlanningPolicy } from "@/api/planningPolicy";
import { useSuppliers, type Supplier } from "@/api/suppliers";

import type { EntityDescriptor } from "./EntityDescriptor";

export const itemEntity: EntityDescriptor<Item> = {
  useEntities: (q) => {
    const r = useItems({ search: q || undefined, limit: 50 });
    return { data: r.data?.results, isLoading: r.isLoading };
  },
  useResolveSelected: (ids) => {
    const r = useItems({ ids, limit: ids.length || 1 });
    return { data: r.data?.results };
  },
  getEntityId: (i) => i.id,
  renderLabel: (i) => `${i.sku} — ${i.name}`,
  bounded: false,
  i18n: {
    searchPlaceholder: "components.entitySelect.item.searchPlaceholder",
    emptyLabel: "components.entitySelect.item.emptyLabel",
  },
};

export const locationEntity: EntityDescriptor<Location> = {
  useEntities: (q) => {
    const r = useLocations({ search: q || undefined });
    return { data: r.data, isLoading: r.isLoading };
  },
  useResolveSelected: (ids) => {
    const { data } = useLocations({});
    return { data: data?.filter((l) => ids.includes(l.id)) };
  },
  useList: () => {
    const r = useLocations({});
    return { data: r.data, isLoading: r.isLoading };
  },
  getEntityId: (l) => l.id,
  renderLabel: (l) => l.name,
  bounded: true,
  i18n: {
    searchPlaceholder: "components.entitySelect.location.searchPlaceholder",
    emptyLabel: "components.entitySelect.location.emptyLabel",
  },
};

// Supplier is unbounded (thousands per tenant): server-side search, no useList.
export const supplierEntity: EntityDescriptor<Supplier> = {
  useEntities: (q) => {
    const r = useSuppliers({ search: q || undefined, limit: 50 });
    return { data: r.data?.results, isLoading: r.isLoading };
  },
  useResolveSelected: (ids) => {
    const r = useSuppliers({ ids, limit: ids.length || 1 });
    return { data: r.data?.results };
  },
  getEntityId: (s) => s.id,
  renderLabel: (s) => s.name,
  bounded: false,
  i18n: {
    searchPlaceholder: "components.entitySelect.supplier.searchPlaceholder",
    emptyLabel: "components.entitySelect.supplier.emptyLabel",
  },
};

export const planningPolicyEntity: EntityDescriptor<PlanningPolicy> = {
  useEntities: (q) => {
    const r = usePlanningPolicies({ search: q || undefined, ordering: "name" });
    return { data: r.data, isLoading: r.isLoading };
  },
  useResolveSelected: (ids) => {
    const { data } = usePlanningPolicies({ ordering: "name" });
    return { data: data?.filter((c) => ids.includes(c.id)) };
  },
  useList: () => {
    const r = usePlanningPolicies({ ordering: "name" });
    return { data: r.data, isLoading: r.isLoading };
  },
  getEntityId: (c) => c.id,
  renderLabel: (c) => c.name,
  bounded: true,
  i18n: {
    searchPlaceholder:
      "components.entitySelect.planningPolicy.searchPlaceholder",
    emptyLabel: "components.entitySelect.planningPolicy.emptyLabel",
  },
};

// ItemLabel is used only in faceted filter bars (browse mode), so it needs no
// combobox i18n. The full list is the faceted option source.
export const itemLabelEntity: EntityDescriptor<ItemLabel> = {
  useEntities: (q) => {
    const { data, isLoading } = useItemLabels();
    const all = data ?? [];
    const filtered = q
      ? all.filter((l) => l.name.toLowerCase().includes(q.toLowerCase()))
      : all;
    return { data: filtered, isLoading };
  },
  useResolveSelected: (ids) => {
    const { data } = useItemLabels();
    return { data: data?.filter((l) => ids.includes(l.id)) };
  },
  useList: () => {
    const r = useItemLabels();
    return { data: r.data, isLoading: r.isLoading };
  },
  getEntityId: (l) => l.id,
  renderLabel: (l) => l.name,
  bounded: true,
};
